import React, { useState, useEffect, useRef } from 'react';
import { Player } from '../types';
import { db, collection, doc, setDoc, getDocs, query, orderBy, where } from '../firebaseConfig';

// ─── TYPER ────────────────────────────────────────────────────────────────────

interface SubjectBlock {
  id: string;
  subjectId?: string;
  subjectName: string;
  notes: string;
  links: { url: string; title: string }[];
}

interface JournalEntry {
  date: string;
  userId: string;
  roomId: string;
  roomName: string;
  visitStart: number;
  visitEnd?: number;
  mood: number;
  good: string;
  hard: string;
  remember: string;
  subjects: SubjectBlock[];
  submittedAt?: number;
}

import { getAgeTier } from '../utils/ageUtils';
import { openInHalls } from '../utils/sso';

interface DailyJournalProps {
  me: Player;
  roomId: string;
  roomName: string;
  visitStart: number;
  onClose: () => void;
  mode?: 'exit' | 'manual';
  previewTier?: 1|2|3|4|5|6|null;
  classroomId?: string;
}

// ─── HJÆLPER ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];
// Kladde-noegle pr. elev pr. dag. Ryddes naar dagen er gemt.
const draftKey = (uid: string) => 'nw_journal_draft_' + uid + '_' + today();

const MOOD_LABELS: Record<number, { emoji: string; label: string; color: string }> = {
  1: { emoji: '😔', label: 'Hård dag',      color: 'border-red-500/40 bg-red-500/10 text-red-400' },
  2: { emoji: '😕', label: 'Lidt svær',     color: 'border-orange-500/40 bg-orange-500/10 text-orange-400' },
  3: { emoji: '😐', label: 'Okay dag',      color: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400' },
  4: { emoji: '🙂', label: 'God dag',       color: 'border-green-500/40 bg-green-500/10 text-green-400' },
  5: { emoji: '🌟', label: 'Fantastisk!',   color: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-400' },
};

// ─── SIMPEL MARKDOWN TOOLBAR TIL CAMPUS ───────────────────────────────────────

const CampusTextarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, placeholder, rows = 3 }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const insert = (before: string, after = '') => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    const newVal = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newVal);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  return (
    <div className="space-y-1">
      {/* Mini toolbar */}
      <div className="flex gap-1 flex-wrap">
        {[
          { label: 'B',  action: () => insert('**', '**'),  cls: 'font-black' },
          { label: 'I',  action: () => insert('_', '_'),    cls: 'italic' },
          { label: '•',  action: () => insert('\n- '),      cls: '' },
          { label: '1.', action: () => insert('\n1. '),     cls: '' },
          { label: '🔗', action: () => insert('[', '](url)'), cls: '' },
        ].map(b => (
          <button
            key={b.label}
            type="button"
            onClick={b.action}
            className={`px-2 py-0.5 bg-white/5 hover:bg-white/15 border border-white/10 rounded text-[11px] text-white/80 hover:text-white transition-all ${b.cls}`}
          >
            {b.label}
          </button>
        ))}
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-500/40 transition-all resize-none placeholder:text-white/45 leading-relaxed"
      />
    </div>
  );
};

// ─── LINK INPUT ───────────────────────────────────────────────────────────────

const LinkInput: React.FC<{
  links: { url: string; title: string }[];
  onChange: (links: { url: string; title: string }[]) => void;
}> = ({ links, onChange }) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');

  const add = () => {
    if (!url.trim()) return;
    onChange([...links, { url: url.trim(), title: title.trim() || url.trim() }]);
    setUrl(''); setTitle('');
  };

  return (
    <div className="space-y-2">
      {links.map((l, i) => (
        <div key={i} className="flex items-center gap-2 bg-indigo-900/20 border border-indigo-500/20 rounded-lg px-3 py-1.5">
          <span className="text-[10px] text-indigo-300 flex-1 truncate">🔗 {l.title}</span>
          <a href={l.url} target="_blank" rel="noopener noreferrer"
            className="text-[9px] text-indigo-400 hover:text-white underline shrink-0">Åbn</a>
          <button onClick={() => onChange(links.filter((_, j) => j !== i))}
            className="text-white/55 hover:text-red-400 text-[11px] ml-1">✕</button>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titel (valgfri)"
          className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white/80 text-[11px] outline-none focus:border-indigo-500/30" />
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..."
          onKeyDown={e => e.key === 'Enter' && add()}
          className="flex-[2] bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white/80 text-[11px] outline-none focus:border-indigo-500/30" />
        <button onClick={add}
          className="px-3 py-1.5 bg-indigo-600/40 hover:bg-indigo-600 border border-indigo-500/30 rounded-lg text-[10px] text-white font-black transition-all">+</button>
      </div>
    </div>
  );
};

// ─── ARKIV VISNING ────────────────────────────────────────────────────────────

const JournalArchive: React.FC<{ userId: string; onClose: () => void }> = ({ userId, onClose }) => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, `daily_reflections/${userId}/entries`), orderBy('visitStart', 'desc'))
        );
        setEntries(snap.docs.map(d => d.data() as JournalEntry));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [userId]);

  const formatDate = (d: string) => {
    const dt = new Date(d);
    return dt.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0a0a1a]/98 border-2 border-indigo-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}>

        <div className="p-6 border-b border-white/10 bg-indigo-900/20 flex justify-between items-center shrink-0">
          <div>
            <h3 className="text-lg font-cinzel text-yellow-500 tracking-widest uppercase">Dagbogsarkiv</h3>
            <p className="text-[10px] font-black uppercase text-indigo-300 mt-0.5">{entries.length} dage gemt</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-3xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
          {loading && <p className="text-center text-white/55 py-8 italic font-cinzel text-sm">Henter arkivet...</p>}
          {!loading && entries.length === 0 && (
            <p className="text-center text-white/55 py-8 italic font-cinzel text-sm">Ingen dage gemt endnu...</p>
          )}
          {entries.map(entry => (
            <div key={entry.date} className="border border-white/10 rounded-2xl overflow-hidden">
              <button
                onClick={() => setOpen(open === entry.date ? null : entry.date)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{MOOD_LABELS[entry.mood]?.emoji || '📝'}</span>
                  <div className="text-left">
                    <p className="text-white text-sm font-bold capitalize">{formatDate(entry.date)}</p>
                    <p className="text-white/60 text-[9px] uppercase tracking-widest">{entry.roomName}</p>
                  </div>
                </div>
                <span className="text-white/55 text-sm">{open === entry.date ? '▲' : '▼'}</span>
              </button>

              {open === entry.date && (
                <div className="px-4 py-3 space-y-3 bg-black/20">
                  {entry.good && (
                    <div><p className="text-[9px] text-green-400 font-black uppercase mb-1">🌟 Gik godt</p>
                      <p className="text-white/70 text-sm">{entry.good}</p></div>
                  )}
                  {entry.hard && (
                    <div><p className="text-[9px] text-orange-400 font-black uppercase mb-1">🧩 Var svært</p>
                      <p className="text-white/70 text-sm">{entry.hard}</p></div>
                  )}
                  {entry.remember && (
                    <div><p className="text-[9px] text-indigo-400 font-black uppercase mb-1">📌 Husk</p>
                      <p className="text-white/70 text-sm">{entry.remember}</p></div>
                  )}
                  {entry.subjects?.map((s, i) => (
                    <div key={i} className="border-t border-white/5 pt-2">
                      <p className="text-[9px] text-yellow-400 font-black uppercase mb-1">📚 {s.subjectName}</p>
                      <p className="text-white/70 text-sm">{s.notes}</p>
                      {s.links?.map((l, j) => (
                        <a key={j} href={l.url} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-indigo-400 hover:text-white underline block">🔗 {l.title}</a>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── HOVED KOMPONENT ──────────────────────────────────────────────────────────

const DailyJournal: React.FC<DailyJournalProps> = ({ me, roomId, roomName, visitStart, onClose, mode = 'manual', previewTier, classroomId }) => {
  const [mood, setMood] = useState(0);
  const [good, setGood] = useState('');
  const [hard, setHard] = useState('');
  const [remember, setRemember] = useState('');
  const [subjects, setSubjects] = useState<SubjectBlock[]>([]);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [subjectNames, setSubjectNames] = useState<Record<string, string>>({});
  const [subjectActivities, setSubjectActivities] = useState<Record<string, {id: string, title: string}[]>>({});
  const [extraSources, setExtraSources] = useState<{id: string, title: string, source: string}[]>([]);
  // 18-8-2026: opgavelisten voksede til 33+ pr. fag og bliver ved. En dropdown
  // er ubrugelig ved den stoerrelse, saa eleven soeger i stedet - med de nyeste
  // oeverst, da hun som regel leder efter det hun lige har lavet.
  const [actSearch, setActSearch] = useState<Record<string, string>>({});
  // act_<13 cifre> baerer oprettelsestidspunktet (199/200 doks). Ingen createdAt
  // findes, saa id'et er den mest paalidelige sortering vi har - order som fallback.
  const actTid = (id: string): number => {
    const m = /^act_(\d{13})$/.exec(String(id || ''));
    return m ? Number(m[1]) : 0;
  };
  const [firstVisitStart, setFirstVisitStart] = useState<number | null>(null);
  // 17-8-2026: EN refleksion pr. dag, flere skrivninger. Uden denne indlaesning
  // startede anden skrivning forfra og overskrev dagens foerste indlaeg.
  useEffect(() => {
    let afbrudt = false;
    (async () => {
      let gemtTid = 0;
      try {
        const { doc: fdoc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(fdoc(db, `daily_reflections/${me.id}/entries`, today()));
        if (afbrudt) return;
        if (snap.exists()) {
          const d: any = snap.data() || {};
          gemtTid = Number(d.submittedAt) || 0;
          if (typeof d.mood === 'number' && d.mood > 0) setMood(d.mood);
          if (typeof d.good === 'string' && d.good) setGood(d.good);
          if (typeof d.hard === 'string' && d.hard) setHard(d.hard);
          if (typeof d.remember === 'string' && d.remember) setRemember(d.remember);
          if (Array.isArray(d.subjects) && d.subjects.length) setSubjects(d.subjects);
          if (typeof d.visitStart === 'number') setFirstVisitStart(d.visitStart);
        }
      } catch (e) { /* intet gemt endnu - tom dagbog */ }
      // Kladde vinder hvis den er nyere end sidste gem (browseren lukkede undervejs).
      try {
        if (afbrudt) return;
        const raw = localStorage.getItem(draftKey(me.id));
        if (!raw) return;
        const k: any = JSON.parse(raw);
        if (!k || Number(k.ts) <= gemtTid) return;
        if (typeof k.mood === 'number' && k.mood > 0) setMood(k.mood);
        if (typeof k.good === 'string' && k.good) setGood(k.good);
        if (typeof k.hard === 'string' && k.hard) setHard(k.hard);
        if (typeof k.remember === 'string' && k.remember) setRemember(k.remember);
        if (Array.isArray(k.subjects) && k.subjects.length) setSubjects(k.subjects);
      } catch (e) { /* ubrugelig kladde - ignorer */ }
    })();
    return () => { afbrudt = true; };
  }, [me.id]);
  // Kladde: skriver ved hver aendring, saa intet gaar tabt hvis fanen lukkes.
  useEffect(() => {
    if (!mood && !good && !hard && !remember && subjects.length === 0) return;
    try {
      localStorage.setItem(draftKey(me.id), JSON.stringify({ ts: Date.now(), mood, good, hard, remember, subjects }));
    } catch (e) { /* fuld kvote - kladde springes over */ }
  }, [me.id, mood, good, hard, remember, subjects]);

  // Hent fagnavne fra Firestore baseret på elevens subjectIds
  useEffect(() => {
    const loadSubjects = async () => {
      const ids = me.classIds?.length ? me.classIds : (classroomId ? [classroomId] : []);
      if (!ids.length) return;
      const names: Record<string, string> = {};
      const activities: Record<string, {id: string, title: string}[]> = {};
      try {
        // Phase 3: laes subjects + topics + activities fra flat collections
        const { getDocs: gd, collection: col, query: q, where: w, doc: firestoreDoc, getDoc } = await import('firebase/firestore');
        for (const classId of ids) {
          // Foretraek flat subjects; fallback til nested classroom.subjects
          let classSubjects: any[] = [];
          try {
            const subjSnap = await gd(q(col(db, 'subjects'), w('classroomId', '==', classId)));
            classSubjects = subjSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
          } catch (e) { /* fallback next */ }
          if (classSubjects.length === 0) {
            const snap = await getDoc(firestoreDoc(db, 'classrooms', classId));
            if (snap.exists()) classSubjects = (snap.data() as any).subjects || [];
          }
          // Hent ogsaa topics+activities for alle subjects i denne class paa én gang
          let classTopics: any[] = [];
          let classActivities: any[] = [];
          try {
            const [tSnap, aSnap] = await Promise.all([
              gd(q(col(db, 'topics'), w('classroomId', '==', classId))),
              gd(q(col(db, 'activities'), w('classroomId', '==', classId))),
            ]);
            classTopics = tSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
            classActivities = aSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
          } catch (e) { /* fallback to nested traversal below */ }
          classSubjects.filter((s: any) => s.isActive !== false).forEach((s: any) => {
            const name = s.name?.da || s.name?.en || s.name || s.id;
            names[s.id] = name;
            const acts: {id: string, title: string}[] = [];
            if (classTopics.length > 0 || classActivities.length > 0) {
              // Flat: activities med subjectId==s.id som ikke er archived
              classActivities.filter((a: any) => a.subjectId === s.id).forEach((a: any) => {
                if (a.isArchived !== true && (!a.visibilityDate || Date.now() >= a.visibilityDate)) {
                  acts.push({ id: a.id, title: a.title?.da || a.title?.en || a.title || a.id });
                }
              });
            } else {
              // Fallback: nested traversal
              (s.topics || []).forEach((t: any) => {
                (t.activities || []).forEach((a: any) => {
                  if (a.isArchived !== true && (!a.visibilityDate || Date.now() >= a.visibilityDate)) {
                    acts.push({ id: a.id, title: a.title?.da || a.title?.en || a.title || a.id });
                  }
                });
              });
            }
            activities[s.id] = acts;
          });
        }
      } catch (e) {
        console.warn('Fag fetch fejlede:', e);
      }
      setSubjectNames(names);
      setSubjectActivities(activities);
    };
    loadSubjects();
  }, [me.classIds?.join('|'), classroomId]);

  // Hent evt. eksisterende entry for i dag
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, `daily_reflections/${me.id}/entries`));
        const existing = snap.docs.find(d => d.id === today());
        if (existing) {
          const data = existing.data() as JournalEntry;
          setMood(data.mood || 0);
          setGood(data.good || '');
          setHard(data.hard || '');
          setRemember(data.remember || '');
          setSubjects(data.subjects || []);
        }
      } catch (e) { /* ingen entry endnu */ }
    };
    load();
  }, [me.id]);

  const addSubjectBlock = (subjectId?: string, name?: string) => {
    const subjectName = name || subjectNames[subjectId || ''] || newSubjectName.trim();
    if (!subjectName) return;
    setSubjects(prev => [...prev, {
      id: `${Date.now()}`,
      subjectId,
      subjectName,
      notes: '',
      links: [],
    }]);
    setNewSubjectName('');
    setShowAddSubject(false);
  };

  const updateSubject = (id: string, updates: Partial<SubjectBlock>) => {
    setSubjects(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const removeSubject = (id: string) => {
    setSubjects(prev => prev.filter(s => s.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entry: JournalEntry = {
        date: today(),
        userId: me.id,
        roomId,
        roomName,
        // Foerste skrivnings starttid bevares - ellers flytter dagen sig ved hver gem.
        visitStart: firstVisitStart != null ? firstVisitStart : visitStart,
        visitEnd: Date.now(),
        mood,
        good,
        hard,
        remember,
        subjects,
        submittedAt: Date.now(),
      };
      // Tomme felter udelades - ellers sletter et tomt felt det der stod i forvejen.
      const payload: any = { ...entry };
      if (!String(good || '').trim()) delete payload.good;
      if (!String(hard || '').trim()) delete payload.hard;
      if (!String(remember || '').trim()) delete payload.remember;
      if (!mood) delete payload.mood;
      if (!Array.isArray(subjects) || subjects.length === 0) delete payload.subjects;
      await setDoc(doc(db, `daily_reflections/${me.id}/entries`, today()), payload, { merge: true });
      try { localStorage.removeItem(draftKey(me.id)); } catch (e) {}
      setSaved(true);
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      console.error('Fejl ved gem:', e);
    } finally {
      setSaving(false);
    }
  };

  const isEmpty = mood === 0 && !good && !hard && !remember && subjects.length === 0;

  return (
    <>
      <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
        <div className="w-full max-w-2xl bg-[#0a0a1a]/98 border-2 border-indigo-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

          {/* HEADER */}
          <div className="p-6 border-b border-white/10 bg-indigo-900/20 flex justify-between items-center shrink-0">
            <div>
              <h3 className="text-lg font-cinzel text-yellow-500 tracking-widest uppercase">Dagens Dagbog</h3>
              <p className="text-[10px] font-black uppercase text-indigo-300 mt-0.5">
                {new Date().toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })} · {roomName}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowArchive(true)}
                className="text-[10px] font-black uppercase text-indigo-400 hover:text-white border border-indigo-500/30 px-3 py-1.5 rounded-full transition-all">
                📚 Arkiv
              </button>
              {mode === 'exit' && (
                <button onClick={onClose} className="text-white/55 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all">
                  Spring over
                </button>
              )}
              {mode === 'manual' && (
                <button onClick={onClose} className="text-white/60 hover:text-white text-3xl leading-none">&times;</button>
              )}
            </div>
          </div>

          {/* INDHOLD */}
          <div className="flex-1 overflow-y-auto p-5 space-y-6 no-scrollbar">

            {/* HUMØR */}
            <div className="space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-white/45">Hvordan var din dag?</p>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setMood(n)}
                    className={`flex-1 min-w-[52px] py-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-1 ${
                      mood === n ? MOOD_LABELS[n].color : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}>
                    <span className="text-2xl">{MOOD_LABELS[n].emoji}</span>
                    <span className={`text-[8px] font-black uppercase ${mood === n ? '' : 'text-white/55'}`}>{MOOD_LABELS[n].label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* TRE GRUNDSPØRGSMÅL — TIER-TILPASSET */}
            {(() => {
              const tier = previewTier || getAgeTier(me.ageGroup);
              
              // Tier 1 — emoji-vælger, tekstfelter er valgfrie
              if (tier === 1) return (
                <div className="space-y-6">
                  {[
                    { emoji: '🎉', q: 'Var det sjovt i dag?', key: 'fun' },
                    { emoji: '💡', q: 'Lærte du noget nyt?', key: 'new' },
                    { emoji: '😤', q: 'Var der noget svært?', key: 'hard' },
                  ].map(item => (
                    <div key={item.key} className="space-y-2">
                      <p className="text-2xl font-black text-white">{item.emoji} {item.q}</p>
                      <div className="flex gap-4">
                        <button
                          onClick={() => setGood(prev => { const d = JSON.parse(prev || '{}'); d[item.key] = 'ja'; return JSON.stringify(d); })}
                          className={`flex-1 py-5 rounded-2xl text-4xl border-4 transition-all ${(JSON.parse(good||'{}'))[item.key]==='ja' ? 'border-green-400 bg-green-400/20' : 'border-white/10 bg-white/5'}`}
                        >👍</button>
                        <button
                          onClick={() => setGood(prev => { const d = JSON.parse(prev || '{}'); d[item.key] = 'nej'; return JSON.stringify(d); })}
                          className={`flex-1 py-5 rounded-2xl text-4xl border-4 transition-all ${(JSON.parse(good||'{}'))[item.key]==='nej' ? 'border-red-400 bg-red-400/20' : 'border-white/10 bg-white/5'}`}
                        >👎</button>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-2">
                    <p className="text-lg font-black text-indigo-300">✏️ Vil du skrive noget?</p>
                    <p className="text-sm text-white/50 italic">Mor eller far kan hjælpe</p>
                    <CampusTextarea value={remember} onChange={setRemember} placeholder="Skriv her..." />
                  </div>
                </div>
              );

              const q = tier === 2 ? [
                { key: 'good', emoji: '🌟', color: 'text-green-400', label: 'Hvad gik godt?', placeholder: 'Noget du er stolt af eller lykkedes...', val: good, set: setGood },
                { key: 'hard', emoji: '🧩', color: 'text-orange-400', label: 'Hvad var svært?', placeholder: 'Noget du sidder fast i...', val: hard, set: setHard },
                { key: 'remember', emoji: '📌', color: 'text-indigo-400', label: 'Hvad vil du huske?', placeholder: 'En vigtig tanke til i morgen...', val: remember, set: setRemember },
              ] : tier === 3 ? [
                { key: 'good', emoji: '✅', color: 'text-green-400', label: 'Hvad gik godt i dag?', placeholder: 'Noget du er stolt af, noget der lykkedes...', val: good, set: setGood },
                { key: 'hard', emoji: '⚡', color: 'text-orange-400', label: 'Hvad var svært eller forvirrede dig?', placeholder: 'Noget du sidder fast i, eller ikke forstod helt...', val: hard, set: setHard },
                { key: 'remember', emoji: '🎯', color: 'text-indigo-400', label: 'Hvad vil du huske til i morgen?', placeholder: 'En vigtig tanke, noget du skal følge op på...', val: remember, set: setRemember },
              ] : [
                { key: 'good', emoji: '✓', color: 'text-green-400', label: 'Dagens succeser', placeholder: 'Hvad lykkedes? Hvad er du tilfreds med?', val: good, set: setGood },
                { key: 'hard', emoji: '△', color: 'text-orange-400', label: 'Udfordringer og refleksioner', placeholder: 'Hvad var udfordrende? Hvad vil du undersøge videre?', val: hard, set: setHard },
                { key: 'remember', emoji: '→', color: 'text-indigo-400', label: 'Næste skridt', placeholder: 'Hvad er din prioritet i morgen?', val: remember, set: setRemember },
              ];
              return (
                <div className="space-y-4">
                  {q.map(item => (
                    <div key={item.key} className="space-y-1.5">
                      <p className={`font-black uppercase tracking-widest ${item.color} ${'text-[10px]'}`}>{item.emoji} {item.label}</p>
                      <CampusTextarea value={item.val} onChange={item.set} placeholder={item.placeholder} />
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* FAGBLOKKE — TIER-TILPASSET */}
            {(() => {
              const tier = previewTier || getAgeTier(me.ageGroup);
              const allSubjectIds = Object.keys(subjectNames);

              if (tier === 1) return (
                <div className="space-y-3">
                  <p className="text-lg font-black text-purple-300">📚 Hvilke fag havde du?</p>
                  <div className="flex flex-wrap gap-2">
                    {allSubjectIds.map(id => (
                      <button key={id} onClick={() => addSubjectBlock(id)}
                        className={`px-4 py-2 rounded-2xl text-sm font-black border-2 transition-all ${subjects.find(s => s.subjectId === id) ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-white/60'}`}>
                        {subjectNames[id]}
                      </button>
                    ))}
                  </div>
                </div>
              );

              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-black uppercase tracking-widest text-white/70">📚 Fagnoter (valgfri)</p>
                    <button onClick={() => setShowAddSubject(!showAddSubject)}
                      className="text-[10px] font-black uppercase text-yellow-400 hover:text-yellow-300 border border-yellow-500/30 px-3 py-1 rounded-full transition-all">
                      + Tilføj fag
                    </button>
                  </div>
                  {showAddSubject && (
                    <div className="bg-black/30 border border-white/10 rounded-2xl p-4 space-y-3">
                      {allSubjectIds.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-[10px] text-white/80 font-black uppercase">Dine fag</p>
                          <div className="flex flex-wrap gap-2">
                            {allSubjectIds.filter(id => !subjects.find(s => s.subjectId === id)).map(id => (
                              <button key={id} onClick={() => addSubjectBlock(id)}
                                className="px-3 py-1.5 bg-indigo-900/40 border border-indigo-500/30 hover:border-indigo-400 rounded-xl text-[10px] text-indigo-200 font-black transition-all">
                                {subjectNames[id]}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input value={newSubjectName} onChange={e => setNewSubjectName(e.target.value)}
                          placeholder="Andet fag (fritekst)..."
                          onKeyDown={e => e.key === 'Enter' && addSubjectBlock(undefined, newSubjectName)}
                          className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-500/30" />
                        <button onClick={() => addSubjectBlock(undefined, newSubjectName)}
                          className="px-4 py-2 bg-indigo-600/40 hover:bg-indigo-600 border border-indigo-500/30 rounded-xl text-[10px] text-white font-black transition-all">
                          Tilføj
                        </button>
                      </div>
                    </div>
                  )}
                  {subjects.map(subject => (
                    <div key={subject.id} className="bg-black/20 border border-white/10 rounded-2xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-white/5 border-b border-white/5">
                        <span className="text-sm font-bold text-yellow-400">📚 {subject.subjectName}</span>
                        <button onClick={() => removeSubject(subject.id)} className="text-white/55 hover:text-red-400 text-sm font-black transition-all">✕</button>
                      </div>
                      <div className="p-4 space-y-3">
                        {/* 18-8-2026: vaelgeren var laast til tier>=3. De yngste har ogsaa
                            opgaver - og det er NEMMERE for dem at vaelge fra en liste end
                            at skrive. Uden den var der heller ingen kobling til opgaven,
                            saa 'aabn opgaven'-knappen dukkede aldrig op for dem. */}
                        {subject.subjectId && subjectActivities[subject.subjectId]?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] text-white/80 font-black uppercase">{tier <= 2 ? 'Hvad lavede du?' : 'Hvilken opgave arbejdede du på?'}</p>
                            {(() => {
                              const alle = (subjectActivities[subject.subjectId] || []).slice()
                                .sort((a: any, b: any) => (actTid(b.id) - actTid(a.id)) || ((a.order ?? 999) - (b.order ?? 999)));
                              const q = (actSearch[subject.id] || '').toLowerCase().trim();
                              const fundne = q ? alle.filter((a: any) => String(a.title || '').toLowerCase().includes(q)) : alle.slice(0, 8);
                              const valgt = alle.find((a: any) => a.id === (subject as any).activityId);
                              if (valgt) return (
                                <div className="flex items-center justify-between gap-2 bg-purple-900/25 border border-purple-500/40 rounded-xl px-3 py-2">
                                  <span className="text-sm text-white truncate">📘 {valgt.title}</span>
                                  <button type="button" onClick={() => updateSubject(subject.id, { activityId: '' } as any)}
                                    className="text-white/60 hover:text-white text-sm shrink-0">✕</button>
                                </div>
                              );
                              return (
                                <div className="space-y-1.5">
                                  {alle.length > 8 && (
                                    <input value={actSearch[subject.id] || ''} onChange={e => setActSearch(prev => ({ ...prev, [subject.id]: e.target.value }))}
                                      placeholder={tier <= 2 ? 'Søg...' : 'Søg efter opgave...'}
                                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-purple-500/50 placeholder:text-white/45" />
                                  )}
                                  <div className="space-y-1 max-h-44 overflow-y-auto">
                                    {fundne.length === 0 ? (
                                      <p className="text-[11px] text-white/50 italic px-1 py-2">Ingen opgaver matcher</p>
                                    ) : fundne.map((a: any) => (
                                      <button key={a.id} type="button"
                                        onClick={() => { updateSubject(subject.id, { activityId: a.id } as any); setActSearch(prev => ({ ...prev, [subject.id]: '' })); }}
                                        className="w-full text-left px-3 py-2 rounded-lg bg-black/40 border border-white/10 hover:border-purple-500/50 text-white text-sm transition truncate">
                                        {a.title}
                                      </button>
                                    ))}
                                  </div>
                                  {!q && alle.length > 8 && (
                                    <p className="text-[10px] text-white/45 italic px-1">Viser de 8 nyeste · søg for at finde flere</p>
                                  )}
                                </div>
                              );
                            })()}
                            <select style={{ display: 'none' }} value={(subject as any).activityId || ''} onChange={e => updateSubject(subject.id, { activityId: e.target.value } as any)}>
                              <option value="">—</option>
                              <optgroup label="Fagopgaver">
                                {subjectActivities[subject.subjectId].map(a => (
                                  <option key={a.id} value={a.id}>{a.title}</option>
                                ))}
                              </optgroup>
                              {extraSources.length > 0 && (
                                <optgroup label="Andre kilder">
                                  {extraSources.map(e => (
                                    <option key={e.id} value={e.id}>{e.title} ({e.source})</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </div>
                        )}
                        {(tier === 3 || tier === 4) && (
                          <div className="flex gap-3 items-center">
                            <p className="text-[10px] text-white/80 font-black uppercase">Er du færdig?</p>
                            {['Ja', 'Nej', 'Næsten'].map(v => (
                              <button key={v} onClick={() => updateSubject(subject.id, { done: v } as any)}
                                className={`px-3 py-1 rounded-lg text-[10px] font-black border transition-all ${(subject as any).done === v ? 'bg-green-600 border-green-400 text-white' : 'bg-white/5 border-white/10 text-white/50'}`}>
                                {v}
                              </button>
                            ))}
                          </div>
                        )}
                        <CampusTextarea value={subject.notes} onChange={v => updateSubject(subject.id, { notes: v })}
                          placeholder={tier <= 2 ? 'Hvad lavede du?' : tier <= 4 ? 'Hvad lavede du? Hvad lærte du?' : 'Refleksion over dagens arbejde...'}
                          rows={tier >= 5 ? 4 : 2} />
                        {tier >= 5 && (
                          <div className="space-y-1">
                            <p className="text-[10px] text-white/80 font-black uppercase">Næste skridt</p>
                            <CampusTextarea value={(subject as any).nextStep || ''} onChange={v => updateSubject(subject.id, { nextStep: v } as any)} placeholder="Hvad er dit næste skridt?" rows={2} />
                          </div>
                        )}
                        <LinkInput links={subject.links} onChange={links => updateSubject(subject.id, { links })} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* 18-8-2026: har eleven valgt en opgave i fagnoterne, kan hun hoppe
                DIREKTE derind. dra_open_activity aabner den praecise opgave i
                AcademyHalls - ikke bare siden. */}
            {subjects.some((sb: any) => (sb as any).activityId) && (
              <div className="bg-black/25 border border-white/10 rounded-2xl p-4 space-y-2">
                <p className="text-[10px] font-black uppercase text-amber-300 tracking-widest">Åbn en opgave du arbejdede på</p>
                <div className="flex flex-wrap gap-2">
                  {subjects.filter((sb: any) => (sb as any).activityId).map((sb: any) => {
                    const act = (subjectActivities[sb.subjectId] || []).find((a: any) => a.id === (sb as any).activityId);
                    return (
                      <button key={sb.id} type="button"
                        onClick={() => { openInHalls('academy_halls', (sb as any).activityId); }}
                        className="px-3 py-1.5 bg-amber-600/25 hover:bg-amber-600/50 border border-amber-500/30 rounded-lg text-[11px] text-amber-100 hover:text-white font-bold transition-all">
                        📘 {act ? act.title : sb.subjectName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* CHRONOS LINK */}
            <div className="bg-indigo-950/30 border border-indigo-500/20 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase text-indigo-300 tracking-widest">Se dine opgaver</p>
                <p className="text-[9px] text-white/55 mt-0.5">Gå til Chronos for overblik og checkbokse</p>
              </div>
              {/* 18-8-2026: gik til /chronos UDEN hash og UDEN login - eleven landede
                  paa forsiden og skulle selv lede. openInHalls minter et token og
                  bygger #/chronos, saa hun er logget ind naar hun ankommer. */}
              <button
                type="button"
                onClick={() => { openInHalls('chronos'); }}
                className="px-4 py-2 bg-indigo-600/40 hover:bg-indigo-600 border border-indigo-500/30 rounded-xl text-[10px] text-indigo-200 hover:text-white font-black uppercase transition-all shrink-0"
              >
                Åbn Chronos →
              </button>
            </div>
          </div>

          {/* FOOTER */}
          <div className="p-4 border-t border-white/10 bg-black/20 shrink-0 flex items-center justify-between gap-3">
            <p className="text-[11px] text-white/60 italic">Alt er valgfrit — gem det du har lyst til</p>
            <button
              onClick={handleSave}
              disabled={saving || saved || isEmpty}
              className={`px-6 py-3 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all flex items-center gap-2 ${
                saved
                  ? 'bg-green-600 text-white'
                  : isEmpty
                  ? 'bg-white/5 border border-white/10 text-white/55 cursor-not-allowed'
                  : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg'
              }`}
            >
              {saved ? '✓ Gemt!' : saving ? 'Gemmer...' : '📜 Gem dagbog'}
            </button>
          </div>
        </div>
      </div>

      {/* ARKIV */}
      {showArchive && <JournalArchive userId={me.id} onClose={() => setShowArchive(false)} />}
    </>
  );
};

export default DailyJournal;